import React, { useEffect, useState } from "react";
import ls from "local-storage";
import auth from "services/auth";

/**
 * useIsAuthenticated
 * -----------------------------------------------------------------------------
 * Reactive read of `auth.isAuthenticated()` (auth = localStorage `userObj` with
 * a `userId`). Re-evaluates whenever the `userObj` key changes — auth.js writes
 * it through the `local-storage` lib, which notifies same-tab `ls.on` listeners
 * on login/logout, and the native `storage` event covers other tabs. Logging in
 * therefore flips the value with no manual reload.
 */
export function useIsAuthenticated() {
  const [isAuthed, setIsAuthed] = useState(() => auth.isAuthenticated());

  useEffect(() => {
    const sync = () => setIsAuthed(auth.isAuthenticated());
    ls.on("userObj", sync);
    window.addEventListener("storage", sync);
    // Cover the window between initial state and effect mount.
    sync();
    return () => {
      ls.off("userObj", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  return isAuthed;
}

/**
 * AuthGate
 * -----------------------------------------------------------------------------
 * Systemic pre-login gate. When the visitor is NOT authenticated it renders the
 * `teaser` ONLY — `children` are never mounted, so the personal/player
 * `api.get` calls inside them never fire for a logged-out browser. When
 * authenticated it renders `children`. The swap is reactive (see the hook), so
 * logging in replaces teaser -> dashboard without a reload.
 *
 *   <AuthGate teaser={<RewardsTeaser {...props} />}>
 *     ...authenticated dashboard content...
 *   </AuthGate>
 */
const AuthGate = ({ teaser = null, children }) => {
  const isAuthed = useIsAuthenticated();
  return <>{isAuthed ? children : teaser}</>;
};

export default AuthGate;
